import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import { OVERLAY_Z_INDEX_CLASS } from 'constants/general';
import { MenuItem } from 'interfaces';
import { cn } from 'utils/cn';
import AngleRight from 'assets/icons/AngleRight';

type TriggerType = 'hover' | 'click';

interface DropdownMenuProps {
  trigger?: TriggerType;
  children: ReactNode;
  subMenus?: MenuItem[];
  className?: string;
  contentClassName?: string;
  onItemClick?: (menuItem: MenuItem) => void;
  renderSubMenuStyle?: (subPath?: string) => string;
  defaultSubMenuStyle?: (subPath?: string) => string;
  renderMenuItem?: (menuItem: MenuItem, onClose?: () => void) => ReactNode;
  renderSubMenuContentClassName?: (menuItem: MenuItem) => string;
  header?: ReactNode;
  headerStyle?: string;
  subTriggerIcon?: ReactNode;
}

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuSub = DropdownMenuPrimitive.Sub;

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        `${OVERLAY_Z_INDEX_CLASS.POPOVER} w-auto overflow-hidden rounded-md border border-transparent dark:border-secondary-dark-500 bg-white dark:bg-secondary-dark-200 p-1 text-gray-900 dark:text-secondary-dark-900 shadow-md`,
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
      'focus:bg-gray-100 dark:focus:bg-secondary-dark-300 focus:text-gray-900 dark:focus:text-secondary-dark-900',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      inset && 'pl-8',
      className
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuSubTriggerComponent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
    icon?: ReactNode;
  }
>(({ className, inset, children, icon, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
      'focus:bg-gray-100 dark:focus:bg-secondary-dark-300 data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-secondary-dark-300 text-gray-900 dark:text-secondary-dark-900',
      '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
      inset && 'pl-8',
      className
    )}
    {...props}>
    {children}
    {icon || (
      <AngleRight styles='ml-auto ~w-0.5/3 h-auto' fill='currentColor' />
    )}
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTriggerComponent.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContentComponent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      `${OVERLAY_Z_INDEX_CLASS.POPOVER} min-w-[8rem] overflow-hidden rounded-md border border-transparent dark:border-secondary-dark-500 bg-white dark:bg-secondary-dark-300 p-1 text-gray-900 dark:text-secondary-dark-900 shadow-lg`,
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
      'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className
    )}
    {...props}
  />
));
DropdownMenuSubContentComponent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

interface SubMenuComponentProps {
  subMenu: MenuItem;
  onItemClick?: (menuItem: MenuItem) => void;
  renderSubMenuStyle?: (subPath?: string) => string;
  defaultSubMenuStyle?: (subPath?: string) => string;
  renderMenuItem?: (menuItem: MenuItem, onClose?: () => void) => ReactNode;
  renderSubMenuContentClassName?: (menuItem: MenuItem) => string;
  onItemSelected?: () => void;
  closeDropdown?: () => void;
  subTriggerIcon?: ReactNode;
}

const SubMenuComponent = ({
  subMenu,
  onItemClick,
  renderSubMenuStyle,
  defaultSubMenuStyle,
  renderMenuItem,
  renderSubMenuContentClassName,
  onItemSelected,
  closeDropdown,
  subTriggerIcon
}: SubMenuComponentProps) => {
  const hasNestedSubMenus = !!subMenu.subMenus?.length;

  const subMenuStyle = renderSubMenuStyle || defaultSubMenuStyle;
  const defaultSubMenuContentClassName =
    `flex flex-col gap-1 bg-primary-200/90 shadow-lg rounded w-full lg:w-56 p-2 ${OVERLAY_Z_INDEX_CLASS.POPOVER}`;
  const subMenuContentClassName =
    renderSubMenuContentClassName?.(subMenu) || defaultSubMenuContentClassName;

  if (hasNestedSubMenus) {
    return (
      <DropdownMenuPrimitive.Sub>
        <DropdownMenuSubTriggerComponent
          className={cn(subMenuStyle?.(subMenu.path), 'flex justify-between')}
          icon={
            subMenu.isLoading ? (
              <span
                className='ml-auto ~w-3/3.5 ~h-3/3.5 rounded-full border-t-2 border-r border-l-2 border-b border-current animate-spin'
                aria-hidden
              />
            ) : (
              subTriggerIcon
            )
          }>
          <span className='flex items-center gap-2 min-w-0'>
            {subMenu.icon}
            <span className='truncate'>{subMenu.text}</span>
          </span>
        </DropdownMenuSubTriggerComponent>
        <DropdownMenuSubContentComponent
          className={subMenuContentClassName}
          sideOffset={4}
          alignOffset={-4}>
          {subMenu.subMenus?.map((nestedMenu, nestedIndex) => (
            <SubMenuComponent
              key={nestedMenu.id || `nested-menu-${nestedIndex}`}
              subMenu={nestedMenu}
              onItemClick={onItemClick}
              renderSubMenuStyle={renderSubMenuStyle}
              defaultSubMenuStyle={defaultSubMenuStyle}
              renderMenuItem={renderMenuItem}
              renderSubMenuContentClassName={renderSubMenuContentClassName}
              subTriggerIcon={subTriggerIcon}
              onItemSelected={() => {
                onItemClick?.(nestedMenu);
              }}
              closeDropdown={closeDropdown}
            />
          ))}
        </DropdownMenuSubContentComponent>
      </DropdownMenuPrimitive.Sub>
    );
  }

  if (renderMenuItem) {
    const handleClose = () => {
      onItemClick?.(subMenu);
      // Call onItemSelected first to ensure dropdown closes
      onItemSelected?.();
      // Also call closeDropdown as additional guarantee
      closeDropdown?.();
    };
    const customRender = renderMenuItem(subMenu, handleClose);
    if (customRender) {
      return (
        <DropdownMenuItem
          onSelect={() => {
            handleClose();
          }}
          asChild>
          <div
            className='w-full'
            role='presentation'
            onClick={(e) => {
              // Only handle clicks on the wrapper itself, not on buttons inside
              // Let button clicks bubble naturally
              if (e.target === e.currentTarget) {
                const firstButton = (
                  e.currentTarget as HTMLElement
                ).querySelector('button');
                if (firstButton) {
                  firstButton.click();
                }
              }
            }}>
            {customRender}
          </div>
        </DropdownMenuItem>
      );
    }
  }

  if (subMenu.path) {
    return (
      <DropdownMenuItem
        onSelect={() => {
          onItemClick?.(subMenu);
          onItemSelected?.();
        }}
        className={cn(subMenuStyle?.(subMenu.path), 'text-left')}
        role='menuitem'
        aria-busy={subMenu.isLoading}>
        <span
          data-action={subMenu.attribAction}
          className='w-full flex items-center gap-2'>
          {subMenu.icon}
          <span className='truncate'>{subMenu.text}</span>
          {subMenu.isLoading ? (
            <span
              className='ml-auto w-3 h-3 rounded-full border-t-2 border-r border-l-2 border-b border-current animate-spin'
              aria-hidden
            />
          ) : null}
        </span>
      </DropdownMenuItem>
    );
  }

  return null;
};

const DropdownMenuComponent = ({
  trigger = 'hover',
  children,
  subMenus,
  className = '',
  contentClassName = '',
  onItemClick,
  renderSubMenuStyle,
  defaultSubMenuStyle,
  renderMenuItem,
  renderSubMenuContentClassName,
  header,
  headerStyle = 'px-4 py-3 border-b border-gray-200 dark:border-secondary-dark-500',
  subTriggerIcon
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHoveringRef = useRef(false);

  // Handle hover trigger - use ref to avoid re-renders
  const handleMouseEnter = useCallback(() => {
    if (trigger === 'hover') {
      isHoveringRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (!open) {
        setOpen(true);
      }
    }
  }, [trigger, open]);

  const handleMouseLeave = useCallback(() => {
    if (trigger === 'hover') {
      isHoveringRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        // Only close if we're still not hovering
        if (!isHoveringRef.current) {
          setOpen(false);
        }
        timeoutRef.current = null;
      }, 200);
    }
  }, [trigger]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle click outside for click trigger
  useEffect(() => {
    if (trigger === 'click' && open) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const isInsideContainer = containerRef.current?.contains(target);
        const portalContent = document.querySelector(
          '[data-radix-popper-content-wrapper]'
        );
        const isInsidePortal = portalContent?.contains(target);

        if (!isInsideContainer && !isInsidePortal) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [trigger, open]);

  const defaultContentStyle = cn(
    `rounded-md p-2 shadow ${OVERLAY_Z_INDEX_CLASS.POPOVER} flex flex-col gap-1 w-fit bg-secondary-50 dark:bg-secondary-dark-200`,
    contentClassName
  );

  if (!subMenus?.length) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full min-w-max', className)}
      onMouseEnter={trigger === 'hover' ? handleMouseEnter : undefined}
      onMouseLeave={trigger === 'hover' ? handleMouseLeave : undefined}>
      <DropdownMenu open={open} modal={false}>
        <DropdownMenuTrigger
          asChild
          onClick={
            trigger === 'click' ? () => setOpen((prev) => !prev) : undefined
          }>
          <div style={{ display: 'inline-block', width: '100%' }}>
            {children}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={defaultContentStyle}
          side='bottom'
          align='start'
          sideOffset={4}
          alignOffset={0}
          collisionPadding={8}
          onMouseEnter={() => {
            if (trigger === 'hover') {
              isHoveringRef.current = true;
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
            }
          }}
          onMouseLeave={() => {
            if (trigger === 'hover') {
              handleMouseLeave();
            }
          }}>
          {header && <div className={headerStyle}>{header}</div>}
          <DropdownMenuGroup>
            {subMenus.map((subMenu, index) => (
              <SubMenuComponent
                key={subMenu.id || `submenu-${index}`}
                subMenu={subMenu}
                onItemClick={onItemClick}
                renderSubMenuStyle={renderSubMenuStyle}
                defaultSubMenuStyle={defaultSubMenuStyle}
                renderMenuItem={renderMenuItem}
                renderSubMenuContentClassName={renderSubMenuContentClassName}
                subTriggerIcon={subTriggerIcon}
                onItemSelected={() => {
                  // Close dropdown when item is selected (for click triggers)
                  if (trigger === 'click') {
                    setOpen(false);
                  }
                }}
                closeDropdown={
                  trigger === 'click' ? () => setOpen(false) : undefined
                }
              />
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default DropdownMenuComponent;
